/**
 * The provider CLIs wrap some transcript lines in their own tags, and those
 * tags reached the screen.
 *
 * On the board, a session's last prompt read `<task-notification>` and another
 * read `<local-command-stdout>Set effort level to ultracode…</local-command-stdout>`
 * — raw markup, in the column that is supposed to say what the operator asked
 * for. It is not wrong data: the harness really did send that. It is a display
 * question, and the answer is to unwrap what has content and name what does not.
 *
 * A wrapper is a *machine* fact, so what is shown for one goes in the mono face
 * (CLAUDE.md), which is also what stops it from being mistaken for something a
 * person or an agent said. The tag name is shown as it is rather than
 * translated: the set of tags is the CLI's, it grows without asking us, and a
 * name is a fact where `session.lastTurn.harness.task_notification` would be a
 * missing key.
 *
 * Anything that is not exactly one of the two shapes below passes through
 * untouched. A half-understood transform that mangles a real sentence is worse
 * than showing the sentence.
 *
 * The same cell also printed raw markdown source — `**CI geçti.**` with its
 * asterisks, and commit hashes wearing their backticks — because an agent's
 * reply is markdown and the board rendered it as a plain string. The fences are
 * not text; they are a *claim about the face*, and this console already has one:
 * machine values are mono (CLAUDE.md). So emphasis is removed and a fenced span
 * becomes a segment the renderer can set in mono, which is what the fence was
 * asking for in the first place.
 */

export interface TurnText {
  /** What to render. */
  text: string
  /** True when this came out of a harness wrapper rather than being said. */
  harness: boolean
}

const WRAPPED = /^<([a-z][a-z0-9-]*)>([\s\S]*)<\/\1>$/
const BARE = /^<([a-z][a-z0-9-]*)\s*\/?>$/

/** Unwraps one harness tag, or names an empty one. Recurses, because the CLIs
 *  nest them — a command's stdout arrives inside its own envelope. */
export function readTurnText(raw: string | undefined, depth = 0): TurnText {
  const text = (raw ?? '').trim()
  if (text === '' || depth > 4) return { text, harness: false }

  const wrapped = WRAPPED.exec(text)
  if (wrapped) {
    const inner = readTurnText(wrapped[2], depth + 1)
    // An envelope with nothing in it is still a fact: something happened and it
    // was that. Name it rather than rendering an empty cell.
    if (inner.text === '') return { text: wrapped[1]!, harness: true }
    return { text: inner.text, harness: true }
  }

  const bare = BARE.exec(text)
  if (bare) return { text: bare[1]!, harness: true }

  return { text, harness: false }
}


/** One run of a line, and whether it was fenced as code. */
export interface TurnSegment {
  text: string
  code: boolean
}

/** Emphasis markers, removed rather than rendered. `**CI geçti.**` reached the
 *  screen with its asterisks; bold is not available in a table cell and the
 *  characters are not what the agent meant to say. */
const EMPHASIS = /(\*\*|__)(?=\S)([\s\S]*?\S)\1/g

/** A trailing ellipsis, in either spelling. The server truncates a long reply
 *  and appends one, and CSS then adds its own — so a cut line ended "… …". */
const TRAILING_ELLIPSIS = /\s*(\.\.\.|…)\s*$/

/**
 * A transcript line, ready to render: harness envelope removed, emphasis
 * stripped, a single trailing ellipsis at most, and split into plain and fenced
 * runs so the fenced ones can take the mono face.
 */
export function turnSegments(raw: string | undefined): {
  segments: TurnSegment[]
  harness: boolean
} {
  const read = readTurnText(raw)
  if (read.text === '') return { segments: [], harness: read.harness }

  const flat = read.text.replace(EMPHASIS, '$2').replace(TRAILING_ELLIPSIS, '')
  const segments: TurnSegment[] = []
  // Backtick runs. An unmatched backtick is left alone: it is a character
  // somebody typed, not an unclosed fence to guess at.
  const fence = /`([^`\n]+)`/g
  let at = 0
  for (let m = fence.exec(flat); m; m = fence.exec(flat)) {
    if (m.index > at) segments.push({ text: flat.slice(at, m.index), code: false })
    segments.push({ text: m[1]!, code: true })
    at = m.index + m[0].length
  }
  if (at < flat.length) segments.push({ text: flat.slice(at), code: false })
  if (segments.length === 0) segments.push({ text: flat, code: false })
  return { segments, harness: read.harness }
}

/** The same line as one string, for a `title` attribute. */
export function turnPlainText(raw: string | undefined): string {
  return turnSegments(raw)
    .segments.map((s) => s.text)
    .join('')
}
